import React, { useState } from "react";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { ForgotPasswordAction } from "../../store/action";
import AuthShell from "../ui/AuthShell";
import {
  fieldClass,
  labelClass,
  primaryButtonClass,
  textLinkClass,
} from "../ui/formStyles";

/**
 * "I forgot my password" — collects an address and asks the API to send a link.
 *
 * The confirmation is deliberately worded as a conditional ("if an account
 * exists"). The API refuses to say whether an address is registered, and
 * phrasing this as "we sent it" would leak on the client what the API took care
 * not to leak on the server.
 */
const ForgotPassword = () => {
  const { t } = useTranslation();
  const [email, setEmail] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [sent, setSent] = useState("");

  const handleSubmit = async (event) => {
    event.preventDefault();
    setSubmitting(true);

    const fallback = t("ForgotPassword.sent");

    try {
      const data = await ForgotPasswordAction(email);
      setSent(data?.message || fallback);
    } catch (error) {
      // Even a failure shows the same message: an error state visible only for
      // real accounts would be the same disclosure by another route.
      setSent(fallback);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <AuthShell className="forgot-content w-full max-w-md">
      <div className="text-center">
        <span className="inline-flex size-12 items-center justify-center rounded-xl bg-brand-50 text-brand-600">
          <i className="bx bx-lock-open-alt text-2xl" aria-hidden="true" />
        </span>
        <h1 className="forgot-title mt-4 text-fluid-xl font-bold tracking-tight text-ink-900">
          {t("ForgotPassword.title")}
        </h1>
        <p className="mt-2 text-fluid-sm text-ink-500">
          {t("ForgotPassword.subtitle")}
        </p>
      </div>

      {sent ? (
        <>
          <p className="mt-7 rounded-lg bg-brand-50 px-4 py-3 text-center text-fluid-sm text-ink-700">
            {sent}
          </p>
          <p className="mt-4 text-center text-xs text-ink-400">
            {t("ForgotPassword.validity")}
          </p>
        </>
      ) : (
        <form onSubmit={handleSubmit} className="mt-8">
          <div className="form-item">
            <label htmlFor="forgot-email" className={labelClass}>
              {t("ForgotPassword.email")}
            </label>
            <input
              type="email"
              id="forgot-email"
              name="email"
              className={fieldClass}
              placeholder={t("ForgotPassword.email")}
              value={email}
              onChange={(event) => setEmail(event.target.value)}
              autoComplete="email"
              required
            />
          </div>

          <div className="form-item mt-7">
            <button type="submit" className={primaryButtonClass} disabled={submitting}>
              {submitting
                ? t("ForgotPassword.submitting")
                : t("ForgotPassword.submit")}
            </button>
          </div>
        </form>
      )}

      <span className="mt-6 block text-center text-fluid-sm text-ink-500">
        {t("ForgotPassword.remembered")}{" "}
        <Link to="/login" className={textLinkClass}>
          {t("ForgotPassword.backToLogin")}
        </Link>
      </span>
    </AuthShell>
  );
};

export default ForgotPassword;
